import React, { useState, useEffect } from 'react';
import Header from '../components/header';
import DefaultFooter from '../components/default-footer';
import { useTranslation } from "react-i18next";
import { API_URL } from "../config";
import KayakFlightSearchInput, { KayakInputStyles } from '../components/kayak-flight-search-input';



function DuneRule({ tone = "light" }: { tone?: "light" | "dark" }) {
  const stroke = tone === "dark" ? "rgba(255,255,255,.14)" : "rgba(41,75,90,.14)";
  return (
    <div aria-hidden="true" style={{ lineHeight: 0, background: "inherit" }}>
      <svg viewBox="0 0 1200 34" width="100%" height="22" preserveAspectRatio="none">
        <path
          d="M0 24 C 90 4, 170 4, 250 20 S 400 30, 480 14 S 620 2, 700 18 S 850 30, 940 12 S 1090 2, 1200 16"
          fill="none" stroke="var(--gold-d)" strokeOpacity="0.55" strokeWidth="1.5"
        />
        <path
          d="M0 28 C 90 12, 170 12, 250 26 S 400 34, 480 20 S 620 10, 700 24 S 850 34, 940 18 S 1090 10, 1200 22"
          fill="none" stroke={stroke} strokeWidth="1"
        />
      </svg>
    </div>
  );
}

// ============================================================
// Content
// ============================================================

const TRUST_ITEMS = [
  { icon: "ticket", labelKey: "trustRealPnr", fallback: "Real Airline PNR" },
  { icon: "clock", labelKey: "trustFastDelivery", fallback: "Delivered in 30 Minutes" },
  { icon: "shield", labelKey: "trustEmbassyAccepted", fallback: "Accepted at Visa Centers" },
  { icon: "globe", labelKey: "trustNoRisk", fallback: "No Payment or Booking Risk" },
];

// ── Validation ───────────────────────────────────────────────────────────
const NAME_REGEX = /^[A-Za-zÀ-ÖØ-öø-ÿ' -]{2,80}$/;
const PASSPORT_REGEX = /^[A-Za-z0-9]{5,15}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^\+?[0-9\s-]{7,16}$/;


// ============================================================
// PAGE
// ============================================================

function DummyTicket() {
  const { t } = useTranslation();
  const [tripType, setTripType] = useState<"oneway" | "roundtrip">("roundtrip");
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [referenceCode, setReferenceCode] = useState<string>("");
  const [form, setForm] = useState({
    fullName: "",
    passportNumber: "",
    nationality: "",
    travelers: "1",
    fromCity: "",
    toCity: "",
    departDate: "",
    returnDate: "",
    email: "",
    phone: "",
    notes: "",
  });

  // ── Authentification ────────────────────────────────────────────────────
  // hasToken est figé au montage : si l'utilisateur se connecte, il revient
  // sur cette page via une navigation normale, qui remonte le composant.
  const [hasToken] = useState<boolean>(() => !!localStorage.getItem("token_customer_silverjarz"));
  const [isGuest, setIsGuest] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);

  useEffect(() => {
    if (!hasToken) {
      setShowAuthModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismissAuthModal = () => {
    setIsGuest(true);
    setShowAuthModal(false);
  };

  // ── Validation state ──────────────────────────────────────────────────
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const validateField = (name: string, value: string): string => {
    switch (name) {
      case "fullName":
        if (!value.trim()) return t("valRequired", "This field is required.");
        if (!NAME_REGEX.test(value.trim())) return t("valFullName", "Enter a valid full name (letters only, 2–80 characters).");
        return "";

      case "passportNumber":
        if (!value.trim()) return t("valRequired", "This field is required.");
        if (!PASSPORT_REGEX.test(value.trim())) return t("valPassport", "Enter a valid passport number (5–15 letters or digits).");
        return "";

      case "nationality":
        if (!value.trim()) return t("valRequired", "This field is required.");
        if (value.trim().length < 2) return t("valNationality", "Enter a valid nationality.");
        return "";

      case "fromCity":
        return value.trim() ? "" : t("valFromCity", "Please select a departure city or airport.");

      case "toCity":
        return value.trim() ? "" : t("valToCity", "Please select a destination city or airport.");

      case "email":
        if (!value.trim()) return t("valRequired", "This field is required.");
        if (!EMAIL_REGEX.test(value.trim())) return t("valEmail", "Enter a valid email address.");
        return "";

      case "phone":
        if (!value.trim()) return t("valRequired", "This field is required.");
        if (!PHONE_REGEX.test(value.trim())) return t("valPhone", "Enter a valid phone number (7–16 digits, may start with +).");
        return "";

      case "departDate": {
        if (!value) return t("valDepartDate", "Please select a departure date.");
        const d = new Date(value);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (d < today) return t("valDepartDatePast", "Departure date can't be in the past.");
        return "";
      }

      case "returnDate": {
        if (tripType !== "roundtrip") return "";
        if (!value) return t("valReturnDate", "Please select a return date.");
        if (form.departDate && value < form.departDate) return t("valReturnDateOrder", "Return date must be on or after the departure date.");
        return "";
      }

      default:
        return "";
    }
  };

  // Revalide en direct uniquement si le champ est déjà en erreur
  const setField = (key: keyof typeof form, value: string) => {
    setForm((f) => ({ ...f, [key]: value }));
    setFieldErrors((prev) => (prev[key] ? { ...prev, [key]: validateField(key, value) } : prev));
  };

  const validateForm = (): boolean => {
    const fields: (keyof typeof form)[] = [
      "fullName", "passportNumber", "nationality",
      "fromCity", "toCity", "departDate", "returnDate",
      "email", "phone",
    ];
    const errors: Record<string, string> = {};
    fields.forEach((f) => {
      errors[f] = validateField(f, form[f] as string);
    });

    setFieldErrors(errors);
    const hasError = Object.values(errors).some(Boolean);
    if (hasError) {
      setSubmitError(t("dtValidationError", "Please complete all required fields correctly before submitting."));
    }
    return !hasError;
  };

  const update = (field: keyof typeof form) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => setField(field, e.target.value);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Garde-fou : impossible de soumettre sans être authentifié, même si le
    // bouton a été réactivé manuellement (devtools, etc.)
    if (!hasToken) {
      setShowAuthModal(true);
      return;
    }

    setSubmitError(null);

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    const token = localStorage.getItem("token_customer_silverjarz");

    const payload = {
      tripType,
      travelers: Number(form.travelers),
      from: form.fromCity,
      to: form.toCity,
      departDate: form.departDate,
      returnDate: tripType === "roundtrip" ? form.returnDate : null,
      fullName: form.fullName,
      passportNumber: form.passportNumber,
      nationality: form.nationality,
      email: form.email,
      phone: form.phone,
      notes: form.notes || null,
    };

    try {
      const response = await fetch(`${API_URL}/dummy-ticket-requests`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.message || "Something went wrong while submitting your request.");
      }

      setReferenceCode(data.reference || "");
      setSubmitted(true);
    } catch (err: any) {
      setSubmitError(err.message || "Failed to submit your request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <KayakInputStyles />
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap');

        :root {
          --teal: #294b5a;
          --teal-d: #1a3140;
          --teal-l: #3b6272;
          --gold: #d3be76;
          --gold-l: #ede4b5;
          --gold-d: #b09a50;
          --cream: #f7f4ee;
          --mist: #edeae2;
          --silver: #8a9aa3;
          --ink: #1a2f38;
          --radius: 10px;
          --radius-sm: 5px;
          --shadow-sm: 0 6px 20px rgba(26, 49, 61, .07);
          --shadow-md: 0 20px 48px rgba(26, 49, 61, .12);
          --shadow-lg: 0 28px 64px rgba(26, 49, 61, .18);
        }
        * { box-sizing: border-box; }
        body { font-family: 'DM Sans', sans-serif; background: var(--cream); -webkit-font-smoothing: antialiased; }
        h1, h2, h3, h4 { font-family: 'Playfair Display', serif; }
        a:focus-visible, button:focus-visible, input:focus-visible, select:focus-visible, textarea:focus-visible { outline: 2px solid var(--gold-d); outline-offset: 2px; }
        @media (prefers-reduced-motion: reduce) { *, *::before, *::after { animation-duration: .01ms !important; transition-duration: .01ms !important; } }

        .eyebrow { display: inline-flex; align-items: center; gap: 10px; font-size: 11px; font-weight: 600; letter-spacing: 1.5px;  color: var(--gold-d); }
        .eyebrow::before { content: ''; width: 22px; height: 2px; background: var(--gold); border-radius: 2px; }
        .eyebrow.light { color: var(--gold-l); }
        .eyebrow.light::before { background: var(--gold-l); opacity: .6; }

        .section-title { font-size: clamp(1.9rem, 3vw, 2.7rem); font-weight: 500; color: var(--ink); line-height: 1.18; letter-spacing: -.01em; margin: 10px 0 12px; }
        .section-title em { font-style: italic; color: var(--teal-l); }
        .section-sub { font-size: 15px; color: var(--silver); font-weight: 300; line-height: 1.7; max-width: 520px; }
        .section-pad { padding: 90px 0; }
        @media (max-width: 991px) { .section-pad { padding: 64px 0; } }
        @media (max-width: 576px) { .section-pad { padding: 48px 0; } }
        .bg-cream-section { background: var(--cream); }
        .bg-white-section { background: #fff; }
        .bg-dark-section { background: var(--teal-d); }

        /* ---------- Hero ---------- */
        .dt-hero { position: relative; background: var(--teal-d); overflow: hidden; padding: 88px 0 64px; }
        .dt-hero::before {
          content: '';
          position: absolute; inset: 0;
          background:
            radial-gradient(circle at 12% 18%, rgba(211,190,118,.14) 0%, transparent 45%),
            radial-gradient(circle at 88% 82%, rgba(211,190,118,.10) 0%, transparent 40%);
          pointer-events: none;
        }
        .dt-hero-inner { position: relative; z-index: 1; max-width: 680px; }
        .dt-hero-h1 { font-weight: 600; color: #fff; line-height: 1.16; font-size: clamp(30px, 3.6vw, 48px); margin: 14px 0 16px; }
        .dt-hero-h1 em { font-style: italic; color: var(--gold); font-weight: 400; }
        .dt-hero-sub { font-size: 15px; color: rgba(255,255,255,.78); font-weight: 300; line-height: 1.75; max-width: 480px; margin-bottom: 30px; }
        .dt-hero-ctas { display: flex; gap: 12px; flex-wrap: wrap; }
        .hbtn-primary { display: inline-flex; align-items: center; gap: 8px; background: var(--gold); color: var(--teal-d); border-radius: 5px; font-size: 12px; font-weight: 700; letter-spacing: .3px; padding: 14px 26px; text-decoration: none; transition: transform .2s, background .2s; box-shadow: 0 10px 28px rgba(211,190,118,.28); border: none; cursor: pointer; }
        .hbtn-primary:hover { background: #e2ce86; transform: translateY(-2px); color: var(--teal-d); }
        .hbtn-ghost { display: inline-flex; align-items: center; background: rgba(255,255,255,0.1); color: #fff; border: 1px solid rgba(255,255,255,0.25); border-radius: 5px; font-size: 12px; font-weight: 600; letter-spacing: .3px; padding: 14px 24px; text-decoration: none; transition: background .2s, border-color .2s; }
        .hbtn-ghost:hover { border-color: #fff; background: rgba(255,255,255,0.2); color: #fff; }

        /* ---------- Trust strip ---------- */
        .trust-strip { background: var(--teal); }
        .trust-grid { display: grid; grid-template-columns: repeat(4, 1fr); }
        @media (max-width: 768px) { .trust-grid { grid-template-columns: repeat(2, 1fr); row-gap: 22px; } }
        .trust-item { display: flex; align-items: center; gap: 12px; padding: 20px 16px; border-right: 1px solid rgba(255,255,255,.1); }
        .trust-item:last-child { border-right: none; }
        @media (max-width: 768px) { .trust-item:nth-child(2n) { border-right: none; } }
        .trust-ico { color: var(--gold); flex-shrink: 0; }
        .trust-label { font-size: 12.5px; font-weight: 500; color: #fff; line-height: 1.35; }

        /* ---------- Reassurance list ---------- */
        .reassure-list { display: grid; gap: 22px; }
        .reassure-item { display: flex; gap: 16px; }
        .reassure-check { width: 34px; height: 34px; border-radius: 50%; background: rgba(211,190,118,.15); color: var(--gold-d); display: flex; align-items: center; justify-content: center; flex-shrink: 0; font-size: 13px; }
        .reassure-title { font-size: 14.5px; font-weight: 600; color: var(--ink); margin-bottom: 4px; }
        .reassure-text { font-size: 13px; color: var(--silver); font-weight: 300; line-height: 1.65; }

        .dt-visual { border-radius: var(--radius); overflow: hidden; box-shadow: var(--shadow-md); aspect-ratio: 4/5; position: relative; }
        .dt-visual img { width: 100%; height: 100%; object-fit: cover; }
        .dt-visual-badge { position: absolute; bottom: 20px; left: 20px; right: 20px; background: rgba(255,255,255,.94); backdrop-filter: blur(6px); border-radius: var(--radius-sm); padding: 16px 18px; display: flex; align-items: center; gap: 12px; }
        .dt-visual-badge .ico { color: var(--gold-d); }
        .dt-visual-badge .txt strong { display: block; font-size: 13px; color: var(--ink); font-weight: 600; }
        .dt-visual-badge .txt span { font-size: 11px; color: var(--silver); }

        /* ---------- How it works ---------- */
        .how-grid { display: grid; grid-template-columns: repeat(4, 1fr); }
        @media (max-width: 768px) { .how-grid { grid-template-columns: repeat(2, 1fr); gap: 40px 20px; } }
        .how-item { text-align: center; padding: 0 20px; }
        .how-num-ring { width: 56px; height: 56px; border-radius: 50%; border: 1px solid var(--gold); display: flex; align-items: center; justify-content: center; margin: 0 auto 20px; background: #fff; box-shadow: 0 0 0 6px var(--cream); }
        .how-num { font-size: 1.25rem; font-weight: 600; color: var(--teal); }
        .how-title { font-size: 14px; font-weight: 600; color: var(--ink); margin-bottom: 8px; }
        .how-text { font-size: 12.5px; color: var(--silver); font-weight: 300; line-height: 1.65; max-width: 210px; margin: 0 auto; }

        /* ---------- Pricing ---------- */
        .price-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
        @media (max-width: 900px) { .price-grid { grid-template-columns: 1fr; max-width: 400px; margin: 0 auto; } }
        .price-card { background: #fff; border: 1px solid rgba(41,75,90,.1); border-radius: var(--radius); padding: 30px 26px; text-align: center; position: relative; transition: transform .3s, box-shadow .3s, border-color .3s; }
        .price-card:hover { transform: translateY(-4px); box-shadow: var(--shadow-md); }
        .price-card.feat { border-color: var(--gold); }
        .price-badge { position: absolute; top: -1px; right: 22px; background: var(--gold); color: var(--teal-d); font-size: 9px; font-weight: 700; letter-spacing: 1.5px;  padding: 6px 14px; border-radius: 0 0 5px 5px; }
        .price-plan-title { font-size: 1.05rem; font-weight: 600; color: var(--teal); margin-bottom: 4px; }
        .price-plan-desc { font-size: 12.5px; color: var(--silver); font-weight: 300; margin-bottom: 18px; }
        .price-num { font-size: 2.2rem; font-weight: 600; color: var(--ink); }
        .price-num sup { font-size: .5em; color: var(--silver); font-family: 'DM Sans', sans-serif; top: -.9em; }
        .price-per { font-size: 11px; color: var(--silver); margin-bottom: 22px; display: block; }
        .price-select-btn { display: block; width: 100%; background: var(--teal); color: #fff; border: none; border-radius: var(--radius-sm); font-size: 11px; font-weight: 700; letter-spacing: .4px; padding: 14px 20px; cursor: pointer; transition: background .2s, transform .2s; }
        .price-select-btn:hover { background: var(--teal-d); transform: translateY(-1px); }
        .price-card.feat .price-select-btn { background: var(--gold); color: var(--teal-d); }
        .price-card.feat .price-select-btn:hover { background: var(--gold-d); }

        /* ---------- Booking form ---------- */
        .form-shell { background: #fff; border-radius: var(--radius); box-shadow: var(--shadow-lg), 0 0 0 1px rgba(211,190,118,.14); overflow: hidden; }
        .form-side { background: var(--teal-d); color: #fff; padding: 44px 38px; position: relative; overflow: hidden; }
        .form-side::before { content: ''; position: absolute; inset: 0; background: radial-gradient(circle at 85% 15%, rgba(211,190,118,.14) 0%, transparent 45%); }
        .form-side-content { position: relative; z-index: 1; }
        .form-side-title { font-size: 1.6rem; font-weight: 500; margin: 12px 0 14px; line-height: 1.25; }
        .form-side-text { font-size: 13px; color: rgba(255,255,255,.72); font-weight: 300; line-height: 1.75; margin-bottom: 28px; }
        .form-side-point { display: flex; align-items: center; gap: 10px; font-size: 12.5px; color: rgba(255,255,255,.85); margin-bottom: 12px; }
        .form-side-point .dot { width: 5px; height: 5px; border-radius: 50%; background: var(--gold); flex-shrink: 0; }
        .form-body { padding: 44px 38px; }

        .trip-toggle { display: inline-flex; background: var(--mist); border-radius: 999px; padding: 4px; margin-bottom: 26px; }
        .trip-toggle button { border: none; background: none; padding: 9px 20px; font-size: 12px; font-weight: 600; border-radius: 999px; cursor: pointer; color: var(--silver); transition: background .2s, color .2s; }
        .trip-toggle button.on { background: var(--teal); color: #fff; }

        .field-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; }
        @media (max-width: 560px) { .field-grid { grid-template-columns: 1fr; } }
        .field { margin-bottom: 18px; }
        .field label { display: block; font-size: 11px; font-weight: 700; letter-spacing: .5px;  color: var(--teal); margin-bottom: 7px; }
        .field input, .field select, .field textarea {
          width: 100%; font-family: 'DM Sans', sans-serif; font-size: 14px; color: var(--ink);
          border: 1px solid var(--mist); border-radius: var(--radius-sm); padding: 12px 14px;
          background: var(--cream); transition: border-color .2s, background .2s;
        }
        .field input:focus, .field select:focus, .field textarea:focus { border-color: var(--gold-d); background: #fff; outline: none; }
        .field textarea { resize: vertical; min-height: 90px; }

        /* Aligne le style du champ Kayak (composant partagé) sur celui des
           autres champs de cette page, quelle que soit sa mise en forme interne. */
        .field .kayak-search-wrapper,
        .field > div {
          width: 100%;
        }
        .field input[type="text"],
        .field input:not([type]) {
          height: auto;
        }

        /* ---------- Validation states ---------- */
        .field.invalid input,
        .field.invalid select,
        .field.invalid textarea {
          border-color: #c0392b !important;
          background: #fdf2f0 !important;
        }
        .field-error {
          font-size: 11.5px;
          color: #c0392b;
          margin: 6px 0 0;
        }

        .form-submit-btn { display: inline-flex; align-items: center; gap: 8px; background: var(--gold); color: var(--teal-d); border: none; border-radius: var(--radius-sm); font-size: 12.5px; font-weight: 700; letter-spacing: .4px; padding: 15px 30px; cursor: pointer; transition: background .2s, transform .2s; margin-top: 6px; }
        .form-submit-btn:hover { background: #e2ce86; transform: translateY(-1px); }
        .form-submit-btn:disabled { opacity: .6; cursor: not-allowed; transform: none; }

        .form-error { background: #fdf2f2; border: 1px solid #f8b4b4; color: #9b1c1c; padding: 12px 16px; border-radius: 8px; margin-bottom: 20px; font-size: 13.5px; }

        .form-success { text-align: center; padding: 40px 20px; }
        .form-success-ico { width: 56px; height: 56px; border-radius: 50%; background: rgba(211,190,118,.15); color: var(--gold-d); display: flex; align-items: center; justify-content: center; margin: 0 auto 18px; }
        .form-success h3 { font-size: 1.3rem; color: var(--ink); margin-bottom: 8px; }
        .form-success p { font-size: 13.5px; color: var(--silver); font-weight: 300; }
        .form-success-ref { display: inline-block; margin-top: 16px; background: var(--cream); border: 1px solid var(--mist); border-radius: 8px; padding: 10px 20px; font-size: 13px; color: var(--teal); font-weight: 600; letter-spacing: .04em; }

        /* ---------- Guest lock notice on submit ---------- */
        .form-submit-row { display: flex; flex-direction: column; align-items: flex-start; gap: 12px; }
        .guest-lock-notice {
          display: flex; align-items: center; gap: 10px;
          background: var(--cream); border: 1px solid var(--mist); border-radius: var(--radius-sm);
          padding: 12px 16px; font-size: 12.5px; color: var(--teal); max-width: 460px;
        }
        .guest-lock-notice .ico {
          width: 30px; height: 30px; border-radius: 50%; background: rgba(211,190,118,.18); color: var(--gold-d);
          display: flex; align-items: center; justify-content: center; flex-shrink: 0;
        }
        .guest-lock-notice a { color: var(--teal); font-weight: 700; text-decoration: underline; text-underline-offset: 2px; }
        .guest-lock-notice a:hover { color: var(--gold-d); }

        /* ---------- FAQ ---------- */
        .faq-list { max-width: 760px; margin: 0 auto; }
        .faq-item { border-bottom: 1px solid rgba(41,75,90,.1); }
        .faq-q { width: 100%; text-align: left; background: none; border: none; cursor: pointer; padding: 22px 4px; display: flex; align-items: center; justify-content: space-between; gap: 20px; font-size: 15px; font-weight: 500; color: var(--ink); }
        .faq-q .chev { color: var(--gold-d); flex-shrink: 0; transition: transform .25s; }
        .faq-item.open .faq-q .chev { transform: rotate(45deg); }
        .faq-a { max-height: 0; overflow: hidden; transition: max-height .3s ease; }
        .faq-item.open .faq-a { max-height: 220px; }
        .faq-a-inner { padding: 0 4px 22px; font-size: 13.5px; color: var(--silver); font-weight: 300; line-height: 1.75; max-width: 640px; }

        /* ---------- WhatsApp CTA strip ---------- */
        .wa-strip { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 20px; background: var(--teal-d); border-radius: var(--radius); padding: 34px 38px; }
        .wa-strip h3 { color: #fff; font-size: 1.25rem; font-weight: 500; margin-bottom: 4px; }
        .wa-strip p { color: rgba(255,255,255,.7); font-size: 13px; font-weight: 300; }
        .wa-btn { display: inline-flex; align-items: center; gap: 9px; background: #25D366; color: #fff; border-radius: 5px; font-size: 12.5px; font-weight: 700; letter-spacing: .3px; padding: 14px 24px; text-decoration: none; transition: transform .2s, background .2s; }
        .wa-btn:hover { background: #1ebe5b; transform: translateY(-2px); color: #fff; }

        /* ---------- AUTH MODAL ---------- */
        .dt-modal-overlay {
          position: fixed; inset: 0; z-index: 9999;
          background: rgba(26, 49, 61, .72);
          backdrop-filter: blur(8px);
          display: flex; align-items: center; justify-content: center;
          padding: 20px;
          animation: dtFadeIn .3s ease-out both;
        }
        .dt-modal {
          background: #fff; border-radius: 20px; max-width: 460px; width: 100%;
          padding: 40px 36px 34px; text-align: center; position: relative;
          border: 1px solid rgba(211,190,118,.35);
          box-shadow: var(--shadow-lg);
          animation: dtScaleUp .35s cubic-bezier(.16,1,.3,1) both;
        }
        .dt-modal::before {
          content: '';
          position: absolute; inset: 0; border-radius: 20px; padding: 1px;
          background: linear-gradient(135deg, rgba(211,190,118,.5), transparent 40%);
          -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          -webkit-mask-composite: xor; mask-composite: exclude;
          pointer-events: none;
        }
        .dt-modal__close {
          position: absolute; top: 14px; right: 14px; width: 32px; height: 32px;
          background: var(--cream); border: 1px solid var(--mist); border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          color: var(--teal-d); cursor: pointer; transition: all .2s ease;
        }
        .dt-modal__close:hover { background: var(--teal-d); color: #fff; border-color: var(--teal-d); }
        .dt-modal__icon {
          width: 76px; height: 76px; border-radius: 50%; margin: 0 auto 22px;
          background: linear-gradient(135deg, var(--teal-d) 0%, var(--teal) 100%);
          display: flex; align-items: center; justify-content: center;
          box-shadow: 0 10px 26px rgba(41,75,90,.3);
          border: 2px solid var(--gold);
          position: relative;
        }
        .dt-modal__icon::after {
          content: ''; position: absolute; inset: -6px; border-radius: 50%;
          border: 1px dashed rgba(211,190,118,.5);
        }
        .dt-modal__eyebrow { display: inline-flex; margin-bottom: 10px; }
        .dt-modal__title { font-size: 22px; font-weight: 600; color: var(--ink); margin-bottom: 10px; line-height: 1.25; }
        .dt-modal__text { font-size: 13.5px; color: var(--silver); font-weight: 300; line-height: 1.7; margin-bottom: 26px; }
        .dt-modal__text strong { color: var(--teal); font-weight: 600; }
        .dt-modal__actions { display: flex; flex-direction: column; gap: 10px; }
        .dt-modal__btn {
          display: inline-flex; align-items: center; justify-content: center; gap: 9px;
          width: 100%; border-radius: var(--radius-sm); font-size: 13.5px; font-weight: 700;
          letter-spacing: .3px; padding: 14px 22px; cursor: pointer; text-decoration: none;
          border: none; transition: all .2s ease;
        }
        .dt-modal__btn-primary { background: var(--gold); color: var(--teal-d); box-shadow: 0 8px 22px rgba(211,190,118,.35); }
        .dt-modal__btn-primary:hover { background: #e2ce86; transform: translateY(-2px); color: var(--teal-d); }
        .dt-modal__btn-secondary { background: transparent; border: 1.5px solid var(--mist); color: var(--silver); }
        .dt-modal__btn-secondary:hover { background: var(--cream); color: var(--ink); }
        .dt-modal__fineprint { display: flex; align-items: center; justify-content: center; gap: 6px; margin-top: 18px; font-size: 11px; color: var(--silver); }
        .dt-modal__fineprint svg { color: var(--gold-d); flex-shrink: 0; }

        @keyframes dtFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes dtScaleUp {
          from { opacity: 0; transform: scale(.93) translateY(10px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>

      {/* ---------- AUTH MODAL ---------- */}
      {showAuthModal && (
        <div className="dt-modal-overlay" onClick={dismissAuthModal}>
          <div className="dt-modal" onClick={(e) => e.stopPropagation()}>
            <button className="dt-modal__close" onClick={dismissAuthModal} aria-label={t("close", "Close")}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
            </button>

            <div className="dt-modal__icon">
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 8a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v2a2 2 0 0 0 0 4v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2a2 2 0 0 0 0-4V8z" />
                <path d="M12 6v.01M12 12v.01M12 18v.01" />
              </svg>
            </div>

            <span className="eyebrow dt-modal__eyebrow">{t("dtModalEyebrow", "Reserve Your Dummy Ticket")}</span>
            <h3 className="dt-modal__title">{t("dtModalTitle", "Sign in to request your reservation")}</h3>
            <p className="dt-modal__text">
              {t(
                "dtModalText",
                "Creating an account keeps your reservation "
              )}
              <strong>{t("dtModalTextStrong", "secure and linked to your file")}</strong>
              {t("dtModalTextEnd", " — so we can verify your request and deliver your PNR safely. You're welcome to browse this page first.")}
            </p>

            <div className="dt-modal__actions">
              <a href="/login" className="dt-modal__btn dt-modal__btn-primary">
                {t("dtModalSignIn", "Sign In / Create Account")}
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
              </a>
              <button type="button" className="dt-modal__btn dt-modal__btn-secondary" onClick={dismissAuthModal}>
                {t("dtModalGuest", "Continue as Guest (browse only)")}
              </button>
            </div>

            <div className="dt-modal__fineprint">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
              {t("dtModalSecurityNote", "Your information is only used to prepare your reservation.")}
            </div>
          </div>
        </div>
      )}

      <Header />

      {/* ---------- HERO ---------- */}
      <section className="dt-hero">
        <div className="container">
          <div className="dt-hero-inner">
            <span className="eyebrow light">{t("visaSupportService", "Visa Support Service")}</span>
            <h1 className="dt-hero-h1">
              {t("dtHeroLine1", "A verifiable flight reservation —")}<br />
              {t("dtHeroLine2", "ready for your visa file")}
            </h1>
            <p className="dt-hero-sub">
              {t("dtHeroSub", "Get a real, airline-issued PNR as proof of onward travel for your Schengen or embassy visa application — delivered by email within 30 minutes, no full ticket payment required.")}
            </p>
            <div className="dt-hero-ctas">
              <a href="#booking-form" className="hbtn-primary">
                {t("reserveNow", "Reserve My Dummy Ticket")}
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
              </a>
              <a href="#how-it-works" className="hbtn-ghost">{t("howItWorks", "How It Works")}</a>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- TRUST STRIP ---------- */}
      <section className="trust-strip">
        <div className="container">
          <div className="trust-grid">
            {TRUST_ITEMS.map((item, i) => (
              <div key={i} className="trust-item">
                <span className="trust-ico">
                  {item.icon === "ticket" && <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M3 8a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v2a2 2 0 0 0 0 4v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2a2 2 0 0 0 0-4V8z" /></svg>}
                  {item.icon === "clock" && <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></svg>}
                  {item.icon === "shield" && <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><path d="M9 12l2 2 4-4" /></svg>}
                  {item.icon === "globe" && <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" /><path d="M12 2a15 15 0 0 1 0 20 15 15 0 0 1 0-20z" /></svg>}
                </span>
                <span className="trust-label">{t(item.labelKey, item.fallback)}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- BOOKING FORM ---------- */}
      <section id="booking-form" className="section-pad bg-cream-section">
        <div className="container">
          <div className="text-center" style={{ marginBottom: 44 }}>
            <span className="eyebrow">{t("reserveYours", "Reserve Yours")}</span>
            <h2 className="section-title">{t("dtFormTitle", "Request your dummy ticket")}</h2>
          </div>

          <div className="form-shell">
            <div className="row g-0">
              <div className="col-lg-4">
                <div className="form-side">
                  <div className="form-side-content">
                    <span className="eyebrow light">{t("dtSideEyebrow", "Before You Submit")}</span>
                    <h3 className="form-side-title">{t("dtSideTitle", "A few things to have ready")}</h3>
                    <p className="form-side-text">{t("dtSideText", "This speeds up issuing your reservation and avoids mismatches with your passport.")}</p>
                    <div className="form-side-point"><span className="dot" />{t("dtSidePoint1", "Full name exactly as on your passport")}</div>
                    <div className="form-side-point"><span className="dot" />{t("dtSidePoint2", "Passport number and nationality")}</div>
                    <div className="form-side-point"><span className="dot" />{t("dtSidePoint3", "Preferred travel dates")}</div>
                    <div className="form-side-point"><span className="dot" />{t("dtSidePoint4", "A valid email to receive your PDF")}</div>
                  </div>
                </div>
              </div>

              <div className="col-lg-8">
                <div className="form-body">
                  {submitted ? (
                    <div className="form-success">
                      <div className="form-success-ico">
                        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M20 6L9 17l-5-5" /></svg>
                      </div>
                      <h3>{t("dtSuccessTitle", "Request received")}</h3>
                      <p>{t("dtSuccessText", "Our team is preparing your reservation. You'll receive the itinerary by email shortly.")}</p>
                      {referenceCode && (
                        <div className="form-success-ref">{referenceCode}</div>
                      )}
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} noValidate>
                      {submitError && <div className="form-error">{submitError}</div>}

                      {!hasToken && isGuest && (
                        <div className="form-error" style={{ background: "var(--cream)", border: "1px solid var(--mist)", color: "var(--teal)" }}>
                          {t("dtGuestBanner", "You're browsing as a guest. Sign in to submit your dummy ticket request.")}
                        </div>
                      )}

                      <div className="trip-toggle" role="tablist" aria-label={t("tripType", "Trip type")}>
                        <button type="button" className={tripType === "oneway" ? "on" : ""} onClick={() => {
                          setTripType("oneway");
                          setFieldErrors((prev) => ({ ...prev, returnDate: "" }));
                        }}>
                          {t("planOneWay", "One-Way")}
                        </button>
                        <button type="button" className={tripType === "roundtrip" ? "on" : ""} onClick={() => setTripType("roundtrip")}>
                          {t("planRoundTrip", "Round-Trip")}
                        </button>
                      </div>

                      <div className="field-grid">
                        <div className={`field ${fieldErrors.fullName ? "invalid" : ""}`}>
                          <label htmlFor="fullName">{t("fullNamePassport", "Full Name (as per passport)")}</label>
                          <input id="fullName" value={form.fullName} onChange={update("fullName")} placeholder={t("fullNamePlaceholder", "e.g. Ahmed Al Balushi")} />
                          {fieldErrors.fullName && <p className="field-error">{fieldErrors.fullName}</p>}
                        </div>
                        <div className={`field ${fieldErrors.passportNumber ? "invalid" : ""}`}>
                          <label htmlFor="passportNumber">{t("passportNumber", "Passport Number")}</label>
                          <input id="passportNumber" value={form.passportNumber} onChange={update("passportNumber")} placeholder="N1234567" />
                          {fieldErrors.passportNumber && <p className="field-error">{fieldErrors.passportNumber}</p>}
                        </div>
                      </div>

                      <div className="field-grid">
                        <div className={`field ${fieldErrors.nationality ? "invalid" : ""}`}>
                          <label htmlFor="nationality">{t("nationality", "Nationality")}</label>
                          <input id="nationality" value={form.nationality} onChange={update("nationality")} placeholder={t("nationalityPlaceholder", "e.g. Omani")} />
                          {fieldErrors.nationality && <p className="field-error">{fieldErrors.nationality}</p>}
                        </div>
                        <div className="field">
                          <label htmlFor="travelers">{t("numberOfTravelers", "Number of Travelers")}</label>
                          <select id="travelers" value={form.travelers} onChange={update("travelers")}>
                            {[1, 2, 3, 4, 5, 6].map((n) => <option key={n} value={n}>{n}</option>)}
                          </select>
                        </div>
                      </div>

                      {/* Départ/Destination : champ Kayak enveloppé dans .field
                          pour hériter du style de cette page et afficher les erreurs. */}
                      <div className="field-grid">
                        <div className={`field ${fieldErrors.fromCity ? "invalid" : ""}`}>
                          <KayakFlightSearchInput
                            label={t("departureCity", "Departure City")}
                            placeholder={t("departureCityPlaceholder", "e.g. Muscat (MCT)")}
                            value={form.fromCity}
                            onChange={(val) => setField("fromCity", val)}
                          />
                          {fieldErrors.fromCity && <p className="field-error">{fieldErrors.fromCity}</p>}
                        </div>
                        <div className={`field ${fieldErrors.toCity ? "invalid" : ""}`}>
                          <KayakFlightSearchInput
                            label={t("destinationCity", "Destination City")}
                            placeholder={t("destinationCityPlaceholder", "e.g. Paris (CDG)")}
                            value={form.toCity}
                            onChange={(val) => setField("toCity", val)}
                          />
                          {fieldErrors.toCity && <p className="field-error">{fieldErrors.toCity}</p>}
                        </div>
                      </div>

                      <div className="field-grid" style={{ marginTop: 18 }}>
                        <div className={`field ${fieldErrors.departDate ? "invalid" : ""}`}>
                          <label htmlFor="departDate">{t("departureDate", "Departure Date")}</label>
                          <input id="departDate" type="date" value={form.departDate} onChange={update("departDate")} />
                          {fieldErrors.departDate && <p className="field-error">{fieldErrors.departDate}</p>}
                        </div>
                        <div className={`field ${fieldErrors.returnDate ? "invalid" : ""}`} style={{ opacity: tripType === "roundtrip" ? 1 : 0.4 }}>
                          <label htmlFor="returnDate">{t("returnDate", "Return Date")}</label>
                          <input id="returnDate" type="date" disabled={tripType === "oneway"} value={form.returnDate} onChange={update("returnDate")} />
                          {fieldErrors.returnDate && <p className="field-error">{fieldErrors.returnDate}</p>}
                        </div>
                      </div>

                      <div className="field-grid">
                        <div className={`field ${fieldErrors.email ? "invalid" : ""}`}>
                          <label htmlFor="email">{t("email", "Email")}</label>
                          <input id="email" type="email" value={form.email} onChange={update("email")} placeholder="you@email.com" />
                          {fieldErrors.email && <p className="field-error">{fieldErrors.email}</p>}
                        </div>
                        <div className={`field ${fieldErrors.phone ? "invalid" : ""}`}>
                          <label htmlFor="phone">{t("whatsappPhone", "WhatsApp / Phone")}</label>
                          <input id="phone" value={form.phone} onChange={update("phone")} placeholder="+968 9xxx xxxx" />
                          {fieldErrors.phone && <p className="field-error">{fieldErrors.phone}</p>}
                        </div>
                      </div>

                      <div className="field">
                        <label htmlFor="notes">{t("additionalNotes", "Additional Notes (optional)")}</label>
                        <textarea id="notes" value={form.notes} onChange={update("notes")} placeholder={t("notesPlaceholder", "Embassy name, appointment date, or any special request")} />
                      </div>

                      <div className="form-submit-row">
                        <button type="submit" className="form-submit-btn" disabled={submitting || !hasToken}>
                          {submitting ? t("submitting", "Submitting...") : t("submitRequest", "Submit Request")}
                          {!submitting && (
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
                          )}
                        </button>

                        {!hasToken && (
                          <div className="guest-lock-notice">
                            <span className="ico">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
                            </span>
                            <span>
                              {t("dtLockNoticeText", "Sign in required to submit — ")}
                              <a href="/login">{t("dtLockNoticeLink", "sign in or create an account")}</a>
                            </span>
                          </div>
                        )}
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* ---------- WHATSAPP CTA ---------- */}
      <section className="section-pad bg-cream-section" style={{ paddingTop: 0 }}>
        <div className="container">
          <div className="wa-strip">
            <div>
              <h3>{t("dtWaTitle", "Need it within the hour?")}</h3>
              <p>{t("dtWaText", "Message us on WhatsApp for urgent same-day reservations.")}</p>
            </div>
            <a className="wa-btn" href="https://wa.me/96893579571" target="_blank" rel="noreferrer">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M17.6 6.32A8.86 8.86 0 0 0 12.05 4a8.89 8.89 0 0 0-7.69 13.3L3 21l3.79-1.32a8.9 8.9 0 0 0 5.25 1.69h.01a8.89 8.89 0 0 0 5.55-15.85zm-5.55 13.65h-.01a7.38 7.38 0 0 1-3.76-1.03l-.27-.16-2.79.97.97-2.72-.18-.28a7.4 7.4 0 1 1 13.72-3.94 7.36 7.36 0 0 1-7.68 7.16zm4.07-5.53c-.22-.11-1.32-.65-1.53-.73s-.35-.11-.5.11-.58.73-.71.88-.26.16-.48.05a6.06 6.06 0 0 1-1.78-1.1 6.66 6.66 0 0 1-1.23-1.53c-.13-.22 0-.34.1-.45s.22-.26.33-.38a1.5 1.5 0 0 0 .22-.37.4.4 0 0 0 0-.38c-.06-.11-.5-1.2-.68-1.65s-.36-.37-.5-.38h-.43a.82.82 0 0 0-.6.28 2.5 2.5 0 0 0-.78 1.86 4.34 4.34 0 0 0 .91 2.3 9.94 9.94 0 0 0 3.8 3.36c.53.23.94.36 1.26.47a3 3 0 0 0 1.4.09 2.3 2.3 0 0 0 1.5-1.06 1.86 1.86 0 0 0 .13-1.06c-.06-.09-.2-.15-.42-.26z" /></svg>
              {t("chatOnWhatsapp", "Chat on WhatsApp")}
            </a>
          </div>
        </div>
      </section>

      <DefaultFooter />
    </>
  );
}

export default DummyTicket;